/*!
 * liveClock.js — the console's words for the beat: clock(), downbeat(), latency()
 * © 2026 Rob Canning — GPLv3
 *
 *   clock()          what the beat follows, the tempo, where the bar is
 *   clock(midi)      follow MIDI clock in (a DAW, a sequencer, Strudel)
 *   clock(link)      Ableton Link through the helper on this computer (olive-link.mjs);
 *                    clock(link, 17005) if it listens on another port
 *   clock(own)       stop following MIDI clock or the helper: the grid is this room's again
 *   downbeat()       the next beat is beat 0, a bar's first: here and in the room
 *   latency(80)      this machine's speakers are heard 80 ms late: notes go earlier
 *   latency()        says what it is, and what the browser reports itself
 *
 * On an Oscilla server started with --link, Link needs no word: the
 * server's beat is Link's (server/linkBridge.js), and clock() says so.
 * clock(link) is for Oscilla Live on a web host, with no server behind it.
 * The others in a room are told of a tempo or a downbeat someone else set
 * (watchRoomBeat), so a change that comes from elsewhere is not a surprise.
 */

import { gridTimeline, gridNow } from "../cues/audio/beatGrid.js";
import { downbeat, onRoomTimeline } from "../cues/audio/roomBeat.js";
import { followMidiClock, midiClockStatus } from "../cues/audio/midiClockGrid.js";
import { outputLatencyMs, setOutputLatencyMs, browserOutputLatencyMs } from "../system/outputLatency.js";
import { startHelperLink, stopHelperLink, helperLinkOn, resumeHelperLink, HELPER } from "./liveHelperLink.js";

/** On a page load: clock(link) joined again if this browser had it on. */
export const resumeClock = log => resumeHelperLink({ log });

const SOURCES = {
  local: "this machine's tempo()",
  room: "the room",
  link: "Ableton Link",
  midi: "MIDI clock",
  osc: "OSC (/oscilla/tempo)",
};

const round = (v, d = 2) => +Number(v).toFixed(d);

/** clock() says; clock(midi) / clock(own) choose. True when done. */
export function clockCue(args, log) {
  const w = String(args ?? "").trim().toLowerCase();
  if (!w) {
    const t = gridTimeline();
    // before a downbeat() lands the beat is still below 0
    const where = t.beat < 0 ? `a bar starts in ${round(-t.beat, 1)} beat${-t.beat < 1.05 ? "" : "s"}`
      : `bar ${Math.floor(t.beat / 4) + 1} beat ${Math.floor(t.beat % 4) + 1}`;
    const shared = gridNow().domain === "server" ? "counted on the room's clock" : "counted on this machine's own clock";
    const midi = midiClockStatus().on ? ", following MIDI clock in" : "";
    const helper = helperLinkOn() ? ", joined to the Link helper" : "";
    log(`clock: ${round(t.bpm)} bpm, ${where}, set by ${SOURCES[t.source] ?? t.source}; ${shared}${midi}${helper}`, "info");
    return true;
  }
  const link = /^link\s*(?:,\s*(\d{2,5}))?$/.exec(w);
  if (link) {
    const address = link[1] ? `ws://127.0.0.1:${link[1]}` : HELPER;
    if (helperLinkOn()) stopHelperLink();
    log(`clock: joining the Link helper at ${address} ...`, "info");
    startHelperLink({ address, log });
    return true;
  }
  if (w === "midi") {
    followMidiClock(true).then(r => log(r.ok
      ? `clock: following MIDI clock in (${r.inputs ?? 0} input${r.inputs === 1 ? "" : "s"}); a start from the source is beat 0`
      : `clock: MIDI not opened -- ${r.why}`, r.ok ? "info" : "err"));
    return true;
  }
  if (w === "own" || w === "off") {
    const was = [midiClockStatus().on && "MIDI clock", helperLinkOn() && "the Link helper"].filter(Boolean);
    followMidiClock(false);
    stopHelperLink();
    log(`clock: ${was.length ? `no longer following ${was.join(" or ")}` : "following nothing outside"}; the grid stays where it is`, "info");
    return true;
  }
  log(`clock() takes midi, link or own -- not ${w}`, "err");
  return false;
}

/** downbeat(): a bar starts on the next beat. */
export function downbeatCue(log) {
  const tl = downbeat();
  log(`downbeat: the next beat is a bar's first${tl.shared ? ", for the whole room" : ""}`, "info");
  return true;
}

/** latency(ms) sets this machine's; latency() says. */
export function latencyCue(args, log) {
  const s = String(args ?? "").trim().replace(/\s*ms$/i, "");
  const browser = browserOutputLatencyMs();
  const said = browser != null ? ` (the browser reports ${browser} ms of its own, allowed for already)` : "";
  if (!s) {
    log(`latency: ${outputLatencyMs()} ms on top of the browser's${said}`, "info");
    return true;
  }
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) {
    log(`latency() takes a number of ms, 0 to 1000 -- not ${s}`, "err");
    return false;
  }
  const set = setOutputLatencyMs(n);
  log(`latency: ${set} ms -- notes go that much earlier, from each sound's next start${said}`, "info");
  return true;
}

/**
 * Say in the console when the room's beat changes from somewhere else:
 * another performer's tempo() or downbeat(), Link, OSC.
 */
export function watchRoomBeat(log, isOpen = () => true) {
  let lastBpm = null;
  return onRoomTimeline((tl, d) => {
    const mine = d.from && d.from === window.localClientName;
    const moved = lastBpm == null || Math.abs(tl.bpm - lastBpm) > 0.05 || tl.beat === 0;
    lastBpm = tl.bpm;
    if (mine || !moved || !isOpen()) return;
    const who = d.from ? ` (${d.from})` : "";
    log(tl.beat === 0 ? `downbeat${who}: a bar starts there, ${round(tl.bpm)} bpm` : `tempo ${round(tl.bpm)} bpm${who}`, "info");
  });
}
